/**
 * VideoTile — V2 video upload card. Sister to AudioTile / UploadTile.
 * Used by the Quick Tap edit form (and any future video-bearing edit
 * form). Mirrors the prototype's `.upload-tile` chrome and reuses the
 * existing VideoJS / native <video> player for playback.
 *
 *   <VideoTile
 *     label="Quick Tap Video"
 *     required
 *     hint="Mux-hosted MP4 / MOV"
 *     value={formData.video_url_preview || formData.quick_tap_video_playback_id}
 *     uploading={!!formData.video_uploading}
 *     onChange={handleVideoUpload}
 *     onLoadedMetadata={handleVideoLoadedData}
 *     onRemove={() => …}
 *   />
 *
 * Three playback modes (auto-detected from `value`):
 *   • blob:/data:           → native <video> with controls (pending upload)
 *   • Mux playback id       → VideoJS HLS player
 *   • direct .mp4/.mov etc. → native <video> with controls
 *
 * Behavior:
 *   • Empty state: dashed cream tile, click → file picker (accepts
 *     video/* by default)
 *   • Populated: video player + Replace + Remove buttons + filename
 *   • `onLoadedMetadata` forwarded through so the parent can capture
 *     duration in the same shape `<audio onLoadedMetadata>` uses
 *   • `Uploading…` overlay while in flight
 */
import React, { useRef } from 'react';
import VideoJS from '../../VideoJS/VideoJS';
import { cn } from '../cn';

/* Mux playback IDs are short alphanumeric tokens — no path, no dot. */
function isMuxPlaybackId(value) {
  return typeof value === 'string' && /^[a-zA-Z0-9]{6,}$/.test(value);
}
function isVideoUrl(value) {
  return typeof value === 'string' && /\.(mp4|webm|ogg|mov|m4v)(\?.*)?$/i.test(value);
}
function isBlobLike(value) {
  return typeof value === 'string' && (value.startsWith('blob:') || value.startsWith('data:'));
}

function deriveFilename(url) {
  if (!url) return '';
  if (isBlobLike(url)) return '(pending upload)';
  if (isMuxPlaybackId(url)) return `mux:${url}`;
  try {
    const u = new URL(url);
    const last = u.pathname.split('/').filter(Boolean).pop();
    return last ? decodeURIComponent(last) : url;
  } catch {
    const last = String(url).split('/').filter(Boolean).pop();
    return last || String(url);
  }
}

const UPLOAD_ICON = (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <polygon points="23 7 16 12 23 17 23 7" />
    <rect x="1" y="5" width="15" height="14" rx="2" ry="2" />
  </svg>
);

export default function VideoTile({
  label,
  required,
  hint,
  value,
  uploading,
  accept = 'video/*',
  onChange,
  onLoadedMetadata,
  onRemove,
  className,
  inputId,
}) {
  const inputRef = useRef(null);
  const hasValue = !!value;

  const openPicker = () => {
    if (inputRef.current) inputRef.current.click();
  };
  const handleFile = (e) => {
    if (onChange) onChange(e);
    e.target.value = '';
  };

  const filename = deriveFilename(value);

  /* Choose the right playback element for whatever we have. */
  const renderPlayer = () => {
    if (!hasValue) return null;
    if (isMuxPlaybackId(value)) {
      // Mux HLS player. VideoJS internally caps its width at 800px and
      // forces a 16:9 frame via the `vjs-16-9` class — so portrait
      // Mux sources letterbox inside the player. That's how the
      // mobile app shows them too, so we accept it and just match
      // the player's natural max-width here.
      return (
        <div style={{ width: '100%', maxWidth: 800, margin: '0 auto' }}>
          <VideoJS playbackID={value} />
        </div>
      );
    }
    // blob: / data: / direct video URLs → native <video controls>.
    // Cap the rendered height so portrait videos don't dominate the
    // form, and let the browser pick the aspect ratio from the
    // source so we don't double-letterbox.
    return (
      <video
        src={value}
        controls
        onLoadedMetadata={onLoadedMetadata}
        style={{
          display: 'block',
          margin: '0 auto',
          maxWidth: '100%',
          maxHeight: 480,
          borderRadius: 6,
          background: '#000',
        }}
      >
        Your browser does not support the video tag.
      </video>
    );
  };

  return (
    <div className={cn('flex flex-col', className)} style={{ minWidth: 0 }}>
      {label && (
        <label
          htmlFor={inputId}
          className="font-condensed font-bold uppercase text-text-light"
          style={{
            fontSize: 11,
            letterSpacing: '0.08em',
            marginBottom: 6,
            display: 'flex',
            alignItems: 'baseline',
            flexWrap: 'wrap',
            gap: 4,
          }}
        >
          <span>{label}</span>
          {required && (
            <span
              className="text-coral"
              style={{
                fontWeight: 400,
                textTransform: 'none',
                letterSpacing: 0,
                fontFamily: 'var(--font-sans)',
              }}
            >
              *
            </span>
          )}
          {hint && (
            <span
              className="text-text-muted"
              style={{
                fontWeight: 400,
                textTransform: 'none',
                letterSpacing: 0,
                fontFamily: 'var(--font-sans)',
                fontSize: 11,
              }}
            >
              — {hint}
            </span>
          )}
        </label>
      )}

      <div
        style={{
          background: 'rgb(var(--color-cream))',
          border: hasValue ? '1px solid rgb(var(--color-sand))' : '1px dashed #D6CCAE',
          borderRadius: 8,
          padding: 14,
          transition: 'all 200ms ease',
          cursor: hasValue ? 'default' : 'pointer',
          minWidth: 0,
          position: 'relative',
        }}
        onClick={hasValue ? undefined : openPicker}
        onMouseEnter={(e) => {
          e.currentTarget.style.borderColor = 'rgb(var(--color-teal-green))';
          e.currentTarget.style.borderStyle = 'solid';
          e.currentTarget.style.background = 'rgb(var(--color-surface))';
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.borderColor = hasValue ? 'rgb(var(--color-sand))' : '#D6CCAE';
          e.currentTarget.style.borderStyle = hasValue ? 'solid' : 'dashed';
          e.currentTarget.style.background = 'rgb(var(--color-cream))';
        }}
      >
        {!hasValue ? (
          <div
            className="flex flex-col items-center"
            style={{
              gap: 8,
              padding: '28px 14px',
              textAlign: 'center',
              color: '#9CA1A8',
            }}
          >
            {UPLOAD_ICON}
            <span style={{ fontSize: 13, fontWeight: 600, color: 'rgb(var(--color-navy))' }}>
              Click to upload video
            </span>
            <span style={{ fontSize: 11 }}>MP4 / MOV / WebM</span>
          </div>
        ) : (
          <div className="flex flex-col" style={{ gap: 10 }}>
            {/* Player frame — no forced aspect ratio so portrait
                sources don't get sandwich-letterboxed. Surface bg
                shows around portrait videos as a soft mat; the
                video itself sets its own height up to 480px. */}
            <div
              style={{
                width: '100%',
                background: 'rgb(var(--color-surface))', /* surface */
                borderRadius: 6,
                overflow: 'hidden',
                position: 'relative',
                display: 'grid',
                placeItems: 'center',
                padding: 6,
              }}
            >
              {renderPlayer()}
            </div>

            <div
              className="text-navy"
              style={{
                fontSize: 12,
                fontWeight: 600,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
              title={filename}
            >
              {filename}
            </div>

            <div
              className="flex"
              style={{ gap: 6 }}
              onClick={(e) => e.stopPropagation()}
            >
              <button
                type="button"
                onClick={openPicker}
                className="font-condensed font-bold uppercase cursor-pointer"
                style={{
                  flex: 1,
                  height: 28,
                  padding: '0 10px',
                  border: '1px solid rgb(var(--color-sand))',
                  borderRadius: 4,
                  background: 'rgb(var(--color-surface))',
                  color: 'rgb(var(--color-navy))',
                  fontSize: 11,
                  letterSpacing: '0.06em',
                }}
              >
                Replace
              </button>
              {onRemove && (
                <button
                  type="button"
                  onClick={onRemove}
                  className="font-condensed font-bold uppercase cursor-pointer"
                  style={{
                    height: 28,
                    padding: '0 10px',
                    border: '1px solid transparent',
                    borderRadius: 4,
                    background: 'transparent',
                    color: '#C4715B',
                    fontSize: 11,
                    letterSpacing: '0.06em',
                  }}
                >
                  Remove
                </button>
              )}
            </div>
          </div>
        )}

        {uploading && (
          <div
            style={{
              position: 'absolute',
              inset: 0,
              background: '#FAF4EAE0',
              display: 'grid',
              placeItems: 'center',
              fontSize: 13,
              color: '#5C7E72',
              fontWeight: 600,
              borderRadius: 8,
              zIndex: 2,
            }}
          >
            Uploading video…
          </div>
        )}
      </div>

      <input
        ref={inputRef}
        id={inputId}
        type="file"
        accept={accept}
        onChange={handleFile}
        style={{
          position: 'absolute',
          width: 1,
          height: 1,
          padding: 0,
          margin: -1,
          overflow: 'hidden',
          clip: 'rect(0, 0, 0, 0)',
          whiteSpace: 'nowrap',
          border: 0,
        }}
      />
    </div>
  );
}
